import { authFetch } from "@/lib/api-client";

export type ChangelogStatus = "PENDING" | "GENERATING" | "READY" | "FAILED";

export type ChangelogChannel = "IN_APP" | "SLACK" | "EMAIL";

export type ChangelogDeliveryStatus = "PENDING" | "SENDING" | "SENT" | "FAILED";

/** Whether a deploy may interrupt users in-app under "big news only". */
export type ChangelogSignificance = "BIG_NEWS" | "SMALL_UPDATE";

export interface ChangelogItem {
  topic: string;
  description: string;
  /** Jira key, rendered as a link when present. */
  ticket: string | null;
}

export interface ChangelogSections {
  new: ChangelogItem[];
  improved: ChangelogItem[];
  fixed: ChangelogItem[];
}

export type ChangelogSectionKey = keyof ChangelogSections;

export interface ChangelogDelivery {
  channel: ChangelogChannel;
  status: ChangelogDeliveryStatus;
  /** Addresses reached; null on channels that have no list of their own. */
  recipientCount: number | null;
  /** Why it failed, or which recipients a partial success missed. */
  note: string | null;
  sentAt: string | null;
  /** Who queued the send; null when the actor cannot be resolved. */
  requestedByName: string | null;
  /** Claimed by a worker that never reported back; only a force retry moves it. */
  isStuck: boolean;
}

export interface ChangelogEntry {
  versionTag: string;
  versionSha: string;
  shortSha: string;
  branch: string;
  deployedAt: string;
  deployedBy: string | null;
  status: ChangelogStatus;
  /** The generator's verdict, or an admin's override while unapproved. */
  significance: ChangelogSignificance;
  headline: string | null;
  commitCount: number;
  generationError: string | null;
  editedAt: string | null;
  editedByName: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  /** Sent back on save so a second editor is told to reload rather than clobbering. */
  updatedAt: string;
  /** Claimed for generation by a worker that never came back; only another run moves it. */
  isGenerationStuck: boolean;
  deliveries: ChangelogDelivery[];
  sections: ChangelogSections;
  itemCount: number;
}

export interface ChangelogPage {
  entries: ChangelogEntry[];
  /** False when the API has no GitHub token, so the tab explains itself. */
  configured: boolean;
  /** Set while a generation or send is in flight, telling the client how soon to refetch. */
  pollAfterMs: number | null;
}

/** Sync also reports what it found, so the tab can say what a check turned up. */
export interface ChangelogSyncResult extends ChangelogPage {
  /** Production releases GitHub returned for this page. */
  detected: number;
  /** Version tags whose notes were just requested. */
  enqueued: string[];
}

/** `TEAM` addresses are derived from Adge access; `MANUAL` ones were typed in. */
export type ChangelogRecipientSource = "TEAM" | "MANUAL";

export interface ChangelogRecipient {
  /** Null for a team address that has never been switched off. */
  id: string | null;
  email: string;
  name: string | null;
  isActive: boolean;
  source: ChangelogRecipientSource;
  addedByName: string | null;
  createdAt: string | null;
}

export interface ChangelogRecipientsPage {
  recipients: ChangelogRecipient[];
  /** False when no webhook is set, so the send dialog can explain the grey-out. */
  slackConfigured: boolean;
}

export interface ChangelogEditInput {
  versionTag: string;
  headline: string | null;
  /** Omitted keeps the stored label. */
  significance?: ChangelogSignificance;
  sections: ChangelogSections;
  expectedUpdatedAt: string;
}

const basePath = (versionTag: string) =>
  `/admin/changelog/${encodeURIComponent(versionTag)}`;

export async function fetchChangelog(): Promise<ChangelogPage> {
  const { data } = await authFetch<ChangelogPage>("/admin/changelog");
  return data;
}

/** Reads GitHub for deploys we have not recorded and starts notes for them. */
export async function syncChangelog(): Promise<ChangelogSyncResult> {
  const { data } = await authFetch<ChangelogSyncResult>("/admin/changelog/sync", {
    method: "POST",
  });
  return data;
}

export async function regenerateChangelogNotes(versionTag: string): Promise<void> {
  await authFetch(`${basePath(versionTag)}/regenerate`, { method: "POST" });
}

export async function saveChangelogEntry(input: ChangelogEditInput): Promise<ChangelogEntry> {
  const { versionTag, ...body } = input;
  const { data } = await authFetch<ChangelogEntry>(basePath(versionTag), {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return data;
}

export async function approveChangelogEntry(
  versionTag: string,
  expectedUpdatedAt: string,
): Promise<ChangelogEntry> {
  const { data } = await authFetch<ChangelogEntry>(`${basePath(versionTag)}/approve`, {
    method: "POST",
    body: JSON.stringify({ expectedUpdatedAt }),
  });
  return data;
}

export async function revokeChangelogApproval(versionTag: string): Promise<ChangelogEntry> {
  const { data } = await authFetch<ChangelogEntry>(`${basePath(versionTag)}/approval`, {
    method: "DELETE",
  });
  return data;
}

/** A send answers before the worker posts, so it also says how soon to look again. */
export interface ChangelogSendResult extends ChangelogEntry {
  pollAfterMs: number | null;
}

/** `force` reclaims a channel stranded in SENDING by a worker that died. */
export async function sendChangelogEntry(
  versionTag: string,
  channels: ChangelogChannel[],
  force = false,
): Promise<ChangelogSendResult> {
  const { data } = await authFetch<ChangelogSendResult>(`${basePath(versionTag)}/send`, {
    method: "POST",
    body: JSON.stringify({ channels, force }),
  });
  return data;
}

export async function fetchChangelogRecipients(): Promise<ChangelogRecipientsPage> {
  const { data } = await authFetch<ChangelogRecipientsPage>("/admin/changelog/recipients");
  return data;
}

export async function addChangelogRecipient(input: {
  email: string;
  name: string | null;
}): Promise<ChangelogRecipientsPage> {
  const { data } = await authFetch<ChangelogRecipientsPage>("/admin/changelog/recipients", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

export async function setChangelogRecipientActive(
  email: string,
  isActive: boolean,
): Promise<ChangelogRecipientsPage> {
  const { data } = await authFetch<ChangelogRecipientsPage>("/admin/changelog/recipients", {
    method: "PATCH",
    body: JSON.stringify({ email, isActive }),
  });
  return data;
}

export async function removeChangelogRecipient(
  recipientId: string,
): Promise<ChangelogRecipientsPage> {
  const { data } = await authFetch<ChangelogRecipientsPage>(
    `/admin/changelog/recipients/${encodeURIComponent(recipientId)}`,
    { method: "DELETE" },
  );
  return data;
}

// ─── Settings ───────────────────────────────────────────────────────
// How the changelog behaves rather than what it says: whether a deploy needs a
// human before it reaches Slack, when a roundup is due, and how notes are
// written. One record for the whole platform — there is one product, one
// repository, and one Slack workspace.

export type ChangelogRoundupCadence = "WEEKLY" | "FORTNIGHTLY" | "SPRINT" | "MANUAL";

export type ChangelogNotesAudience = "USER_FACING" | "TECHNICAL";

/** The single gate on what reaches users inside the app. */
export type ChangelogUserFacingAnnouncements = "HOLD" | "BIG_NEWS_ONLY" | "EVERYTHING";

export interface ChangelogSettings {
  requireDeployApproval: boolean;
  requireRoundupApproval: boolean;
  /** Grace period before an unattended post goes out. Zero posts immediately. */
  autoPublishDelayMinutes: number;
  quietHoursEnabled: boolean;
  /** Minutes from local midnight. A start after the end wraps past midnight. */
  quietHoursStartMinute: number;
  quietHoursEndMinute: number;
  /** IANA zone the quiet window is read in. */
  timezone: string;
  /** User-facing changes a deploy needs before it is worth announcing unattended. */
  minItemsToAutoPublish: number;
  /** What users see in-app, independent of how automatic internal channels are. */
  userFacingAnnouncements: ChangelogUserFacingAnnouncements;
  /** Draft a roundup automatically once the cadence window closes. */
  autoComposeRoundups: boolean;
  /** Deploys an automatic roundup needs before it is worth drafting. */
  minDeploysToAutoCompose: number;
  /** Review window between an unattended approval and the send. */
  roundupAutoSendDelayMinutes: number;
  /** Channels an unattended roundup goes to. */
  roundupNotifyEmail: boolean;
  roundupNotifySlack: boolean;
  sprintLengthDays: number;
  /** `YYYY-MM-DD`. The first day of a sprint whose number is known. */
  sprintAnchorDate: string;
  sprintAnchorNumber: number;
  roundupCadence: ChangelogRoundupCadence;
  notesAudience: ChangelogNotesAudience;
  includeJiraLinks: boolean;
  maxItemsPerSection: number;
  autoSyncOnOpen: boolean;
  notifyOnFailure: boolean;
  /** Null until somebody has saved — the page is showing defaults. */
  updatedByName: string | null;
  updatedAt: string | null;
  /** False when no webhook is set, so the page can warn before approval is switched off. */
  slackConfigured: boolean;
  /** The admin-alerts webhook, which the failure alert uses — a separate channel. */
  alertsSlackConfigured: boolean;
}

/** The fields a save may change. The credit and the Slack flags are read-only. */
export type ChangelogSettingsInput = Omit<
  ChangelogSettings,
  "updatedByName" | "updatedAt" | "slackConfigured" | "alertsSlackConfigured"
>;

export async function fetchChangelogSettings(): Promise<ChangelogSettings> {
  const { data } = await authFetch<ChangelogSettings>("/admin/changelog/settings");
  return data;
}

/** Partial: the page sends the fields that changed, not the whole record. */
export async function updateChangelogSettings(
  input: Partial<ChangelogSettingsInput>,
): Promise<ChangelogSettings> {
  const { data } = await authFetch<ChangelogSettings>("/admin/changelog/settings", {
    method: "PUT",
    body: JSON.stringify(input),
  });
  return data;
}

/** Posts a throwaway Slack message so an admin can prove the webhook works. */
export async function sendChangelogSlackTest(): Promise<void> {
  await authFetch("/admin/changelog/slack-test", { method: "POST" });
}

/** The same proof for the admin-alerts webhook, which is a different channel. */
export async function sendAlertsSlackTest(): Promise<void> {
  await authFetch("/admin/changelog/alerts-slack-test", { method: "POST" });
}

// ─── Digests ────────────────────────────────────────────────────────
// A roundup curated from several deploys. Deploys go to Slack, where the detail
// is wanted; roundups go in-app and to the mailing list, where it is not.

export interface ChangelogDigestItem extends ChangelogItem {
  /** Entry items this line was merged from; empty when a human wrote it. */
  sourceItemIds: string[];
}

export interface ChangelogDigestSections {
  new: ChangelogDigestItem[];
  improved: ChangelogDigestItem[];
  fixed: ChangelogDigestItem[];
}

export interface ChangelogDigest {
  id: string;
  title: string;
  status: ChangelogStatus;
  headline: string | null;
  generationError: string | null;
  editedAt: string | null;
  editedByName: string | null;
  approvedAt: string | null;
  approvedByName: string | null;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string;
  /** Claimed for merging by a worker that never came back; only another run moves it. */
  isGenerationStuck: boolean;
  /** The deploys covered, newest first. */
  entries: Array<{ versionTag: string; deployedAt: string }>;
  deliveries: ChangelogDelivery[];
  sections: ChangelogDigestSections;
  itemCount: number;
}

export interface ChangelogDigestPage {
  digests: ChangelogDigest[];
  pollAfterMs: number | null;
}

export interface ChangelogCandidateItem {
  id: string;
  section: ChangelogSectionKey;
  topic: string;
  description: string;
  ticket: string | null;
}

export interface ChangelogCandidateEntry {
  versionTag: string;
  deployedAt: string;
  headline: string | null;
  items: ChangelogCandidateItem[];
}

export interface ChangelogCandidatesPage {
  entries: ChangelogCandidateEntry[];
}

export interface ChangelogDigestEditInput {
  digestId: string;
  title: string;
  headline: string | null;
  sections: ChangelogSections;
  expectedUpdatedAt: string;
}

const digestPath = (digestId: string) =>
  `/admin/changelog/digests/${encodeURIComponent(digestId)}`;

export async function fetchChangelogDigests(): Promise<ChangelogDigestPage> {
  const { data } = await authFetch<ChangelogDigestPage>("/admin/changelog/digests");
  return data;
}

/** Deploys not yet rolled up, with their items, for the compose screen. */
export async function fetchChangelogCandidates(): Promise<ChangelogCandidatesPage> {
  const { data } = await authFetch<ChangelogCandidatesPage>(
    "/admin/changelog/digests/candidates",
  );
  return data;
}

export interface ChangelogDigestSendResult extends ChangelogDigest {
  pollAfterMs: number | null;
}

export async function composeChangelogDigest(input: {
  title: string;
  itemIds: string[];
}): Promise<ChangelogDigestSendResult> {
  const { data } = await authFetch<ChangelogDigestSendResult>("/admin/changelog/digests", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

export async function regenerateChangelogDigest(digestId: string): Promise<void> {
  await authFetch(`${digestPath(digestId)}/regenerate`, { method: "POST" });
}

export async function saveChangelogDigest(
  input: ChangelogDigestEditInput,
): Promise<ChangelogDigest> {
  const { digestId, ...body } = input;
  const { data } = await authFetch<ChangelogDigest>(digestPath(digestId), {
    method: "PUT",
    body: JSON.stringify(body),
  });
  return data;
}

export async function approveChangelogDigest(
  digestId: string,
  expectedUpdatedAt: string,
): Promise<ChangelogDigest> {
  const { data } = await authFetch<ChangelogDigest>(`${digestPath(digestId)}/approve`, {
    method: "POST",
    body: JSON.stringify({ expectedUpdatedAt }),
  });
  return data;
}

export async function revokeChangelogDigestApproval(
  digestId: string,
): Promise<ChangelogDigest> {
  const { data } = await authFetch<ChangelogDigest>(`${digestPath(digestId)}/approval`, {
    method: "DELETE",
  });
  return data;
}

export async function sendChangelogDigest(
  digestId: string,
  channels: ChangelogChannel[],
  force = false,
): Promise<ChangelogDigestSendResult> {
  const { data } = await authFetch<ChangelogDigestSendResult>(`${digestPath(digestId)}/send`, {
    method: "POST",
    body: JSON.stringify({ channels, force }),
  });
  return data;
}

export async function deleteChangelogDigest(digestId: string): Promise<void> {
  await authFetch(digestPath(digestId), { method: "DELETE" });
}

export interface ChangelogDigestEmailPreview {
  from: string;
  to: string;
  recipientCount: number;
  subject: string;
  html: string;
  text: string;
}

export async function fetchChangelogDigestEmailPreview(
  digestId: string,
): Promise<ChangelogDigestEmailPreview> {
  const { data } = await authFetch<ChangelogDigestEmailPreview>(
    `${digestPath(digestId)}/email-preview`,
  );
  return data;
}
